import { useState, useEffect, useCallback, useRef, useTransition, useDeferredValue } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type {
  HomeIntelligenceData,
  HomeIntelligenceError,
  HomeIntelligenceErrorType
} from '../../shared/intelligence/types.js';

interface UseHomeIntelligenceProps {
  address: string;
  enabled?: boolean;
}

interface UseHomeIntelligenceReturn {
  intelligence: HomeIntelligenceData | null;
  deferredIntelligence: HomeIntelligenceData | null;
  isLoading: boolean;
  isTransitioning: boolean;
  error: HomeIntelligenceError | null;
  isStale: boolean;
  refetch: () => Promise<void>;
  lastUpdated: string | null;
}

interface CachedIntelligenceData {
  data: HomeIntelligenceData;
  timestamp: number;
  address: string;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes client-side cache
const MAX_CACHE_SIZE = 10;

// Use a global cache with automatic cleanup
class IntelligenceCache {
  private cache = new Map<string, CachedIntelligenceData>();
  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  set(key: string, data: CachedIntelligenceData) {
    // Clear existing timer if any
    const existingTimer = this.timers.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set data
    this.cache.set(key, data);

    // Set auto-cleanup timer
    const timer = setTimeout(() => {
      this.cache.delete(key);
      this.timers.delete(key);
    }, CACHE_DURATION);
    this.timers.set(key, timer);

    // Maintain cache size limit
    if (this.cache.size > MAX_CACHE_SIZE) {
      const entries = Array.from(this.cache.entries());
      entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
      const toDelete = entries.slice(MAX_CACHE_SIZE);
      toDelete.forEach(([cacheKey]) => {
        this.cache.delete(cacheKey);
        const timer = this.timers.get(cacheKey);
        if (timer) {
          clearTimeout(timer);
          this.timers.delete(cacheKey);
        }
      });
    }
  }

  get(key: string): CachedIntelligenceData | undefined {
    const data = this.cache.get(key);
    if (data && Date.now() - data.timestamp < CACHE_DURATION) {
      return data;
    }
    // Auto-cleanup expired data
    if (data) {
      this.cache.delete(key);
      const timer = this.timers.get(key);
      if (timer) {
        clearTimeout(timer);
        this.timers.delete(key);
      }
    }
    return undefined;
  }

  clear() {
    // Clear all timers
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
    this.cache.clear();
  }
}

const intelligenceCache = new IntelligenceCache();

export function useHomeIntelligence({
  address,
  enabled = true
}: UseHomeIntelligenceProps): UseHomeIntelligenceReturn {
  const { isAuthenticated } = useAuth();
  const [intelligence, setIntelligence] = useState<HomeIntelligenceData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<HomeIntelligenceError | null>(null);
  const [isStale, setIsStale] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  // Use deferred value for stale data to prevent unnecessary re-renders
  const deferredIntelligence = useDeferredValue(intelligence);

  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef<boolean>(true);

  const fetchIntelligence = useCallback(async (showLoading = true) => {
    // Enhanced validation
    const trimmedAddress = address?.trim() || '';
    if (!trimmedAddress || !enabled || !isAuthenticated) {
      return;
    }

    // Check client-side cache first
    const cachedData = intelligenceCache.get(trimmedAddress);
    if (cachedData) {
      setIntelligence(cachedData.data);
      setLastUpdated(new Date(cachedData.timestamp).toISOString());
      setIsStale(false);
      setError(null);
      return;
    }

    // Cancel previous request safely
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Only proceed if component is still mounted
    if (!mountedRef.current) {
      return;
    }

    abortControllerRef.current = new AbortController();

    try {
      if (showLoading) {
        setIsLoading(true);
      }
      setError(null);

      const url = new URL('/api/properties/intelligence', window.location.origin);
      url.searchParams.set('address', trimmedAddress);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        // Validate the response data structure before using
        const data = result.data;
        if (typeof data === 'object' && data.propertyScore && data.lastUpdated) {
          const intelligenceData: HomeIntelligenceData = data;

          // Use transition for non-urgent state updates
          startTransition(() => {
            if (mountedRef.current) {
              setIntelligence(intelligenceData);
              setIsStale(false);
              setLastUpdated(intelligenceData.lastUpdated);
            }
          });

          // Update client-side cache with automatic cleanup
          intelligenceCache.set(trimmedAddress, {
            data: intelligenceData,
            timestamp: Date.now(),
            address: trimmedAddress,
          });
        } else {
          throw new Error('Invalid intelligence data structure received from API');
        }
      } else {
        throw new Error(result.error?.message || 'Failed to fetch intelligence data');
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        return; // Request was cancelled, ignore
      }

      console.error('Error fetching home intelligence:', err);

      const error: HomeIntelligenceError = {
        type: 'NETWORK_ERROR' as HomeIntelligenceErrorType,
        message: err instanceof Error ? err.message : 'Unknown error occurred',
      };
      setError(error);
      setIsStale(false);
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [address, enabled, isAuthenticated, startTransition]);

  const refetch = useCallback(async () => {
    await fetchIntelligence(true);
  }, [fetchIntelligence]);

  // Fetch intelligence data when address, enabled, or token changes
  useEffect(() => {
    fetchIntelligence();
  }, [fetchIntelligence]);

  // Cleanup on unmount - fix race conditions
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  return {
    intelligence,
    deferredIntelligence,
    isLoading,
    isTransitioning: isPending,
    error,
    isStale,
    refetch,
    lastUpdated,
  };
}